<script module lang="ts">
  /** A development build under Vite (and Vitest); false anywhere else. */
  function isDev(): boolean {
    return (import.meta as { readonly env?: { readonly DEV?: boolean } }).env?.DEV === true;
  }
</script>

<script lang="ts">
  /**
   * The chrome around a board: the house's in-game screen, with the game's
   * board (its stage or canvas, which the game and its themes keep owning)
   * in the middle.
   *
   * On a phone it is one column the height of the screen, top to bottom:
   *
   * 1. the top bar (`GameTopBar`): the menu button, `hud` in the centre,
   *    `trailing` on the right, and an optional full-width `strip` under them
   *    (since 1.7.0 also `leading`, left of the menu button; since 1.8.0
   *    `menuGlyph`, the button's glyph: `menu` or `close`).
   *    A game that keeps its bar in a component of its own passes `top`
   *    instead, which renders in the bar's place (that component renders a
   *    `GameTopBar`, so the bar is still the shell's; `onmenu` is then unused);
   * 2. `above`: the game's rows between the bar and the board (a partner
   *    strip, a status lane, a duel's opponent board);
   * 3. `children`: the stage. It takes every pixel the rest leave and never
   *    scrolls the page: anything taller scrolls inside it;
   * 4. `bottom`: free-form. TwinTen passes a line or two and a `ToolRow` of
   *    `ToolButton`s; Interlock passes its touch pads, arranged its own way.
   *
   * From 1024px, `wide` decides:
   *
   * - `split` (default): the stage on the left, the full height; everything
   *   else stacks down a 21.25rem panel on the right in the order above,
   *   followed by `side` (key hints, a desktop-only Pause and End). Each
   *   panel row is exactly as tall as its content (the board never shares a
   *   row with the bar, so the bar is never stretched to the board's height).
   * - `column`: one centred column, `--ui-column` wide, as on a phone.
   *
   * `side` is drawn only beside the board in `split` from 1024px.
   *
   * **A phone on its side** (`landscapeSplit`, since 1.5.0, opt-in): below
   * 1024px, on a screen at most 30rem tall in landscape (`(max-height:
   * 30rem) and (orientation: landscape)`), the stage goes left, sized by the
   * height (as wide as the screen is tall, at most 58% of its width), and the
   * top bar, `above` and `bottom` stack down a panel on the right, `bottom`
   * at its foot. `side` stays hidden there. The notch's sides are kept clear.
   * The top bar's API is unchanged; without the prop a landscape phone gets
   * the phone's column, as before.
   *
   * **The run back-guard.** Pass `guard` and, while `guard.live` is true,
   * Back asks `guard.title` in a `ConfirmDialog` instead of leaving
   * (`BackGuard`): "Leave" calls `guard.onleave`, "Keep playing" (or Escape,
   * Back, the backdrop) re-arms it. The game's own Leave button calls
   * `requestLeave()` (bind the component): it returns `false` when nothing
   * is live, and then the game simply leaves. A game with a history guard of
   * its own leaves `guard` out. `guard.onasking` (since 1.3.0) is told
   * `true` when the question opens and `false` when it closes, by any path
   * (Leave, Keep playing, Escape, Back, `live` turning false, the frame
   * going away), so a solo run can pause while it asks. Keep `guard.live`
   * true through that pause: `live` turning false withdraws the question.
   *
   * **Two pad sets** (`pads`, since 1.9.0, opt-in): two players on one
   * device, each with a set of touch pads (the game's own snippets, player
   * 1's first). `pads` replaces `bottom` (passing both draws `pads`, and says
   * so once in the console in development; `GameFramePadsProps` is the
   * strict shape). Each set is drawn in a slot of its own, a `role="group"`
   * named by `padsLabels` (default the shell's "Player 1 controls" and
   * "Player 2 controls"), in that order for the keyboard, with
   * `touch-action: none` (the stage keeps its own). While pads are drawn
   * the pads' layout is the frame's at every size: `wide` and
   * `landscapeSplit` do not apply, and `side` is not drawn.
   *
   * - `padsLayout: 'sides'` (default): player 1's set in a column on the
   *   left, player 2's on the right, each as wide as the set asks (at most
   *   30% of the width; a wider set is never squeezed: its column grows and
   *   the stage shrinks), running the full height; the top bar, `above` and
   *   the stage in the middle. Each column keeps its own side's safe-area
   *   inset, the top and the bottom inset; the middle keeps the top inset
   *   (the bar's) and the bottom one (the stage's). Below 600px (a phone
   *   upright) the two sets share the bottom row instead, left half and right
   *   half, the bottom inset under both.
   * - `padsLayout: 'ends'`: players across from each other (a tablet flat on
   *   a table): player 2's set along the top, turned 180° (the game draws it
   *   upright; only the set inside the slot turns, so the slot's insets stay
   *   where the screen's edges are), then the bar, `above`, the stage, and
   *   player 1's set along the bottom. The top set keeps the top inset, the
   *   bottom set the bottom inset, everything the side insets. Focus still
   *   runs player 1's set, then player 2's, though player 2's is drawn on
   *   top, and that is intended: the document order is the meaningful order
   *   (WCAG 1.3.2 Meaningful Sequence, 2.4.3 Focus Order). The two sets are
   *   two players' equal groups, not a reading sequence down the screen;
   *   player 1 first is the order the game counts them in, the order of
   *   `pads` and `padsLabels`, and the same in both layouts, so a keyboard or
   *   screen-reader user meets them the same way whichever way the tablet
   *   lies. Only the visual position moves (CSS `order`).
   *
   * Two boards (a duel on one device) are the game's: it draws both in
   * `children`.
   *
   * Test ids (`testids`, each with a default): `frame` `ui-game-frame`,
   * `topBar` `ui-game-top-bar`, `menu` `ui-game-menu`, `above`
   * `ui-game-above`, `stage` `ui-game-stage`, `bottom` `ui-game-bottom`,
   * `side` `ui-game-side`, `pads1` `ui-game-pads-1`, `pads2`
   * `ui-game-pads-2`; the guard's dialog takes `guard.testid`.
   */
  import { type Snippet, untrack } from 'svelte';

  import { BackGuard } from '../backGuard.js';
  import ConfirmDialog from '../components/ConfirmDialog.svelte';
  import GameTopBar from '../components/GameTopBar.svelte';
  import { shellText } from '../i18n.svelte.js';
  import type { GameGuard, GamePadsLayout } from './playTypes.js';

  interface Props {
    readonly onmenu: () => void;
    /** The menu button's name. Default the shell's `gameMenu` ("Game menu"). */
    readonly menuLabel?: string | undefined;
    readonly hud?: Snippet | undefined;
    readonly trailing?: Snippet | undefined;
    readonly strip?: Snippet | undefined;
    /** Left of the menu button (since 1.7.0): `GameTopBar`'s `leading`. */
    readonly leading?: Snippet | undefined;
    /** The menu button's glyph (since 1.8.0): `GameTopBar`'s `menuGlyph`. */
    readonly menuGlyph?: 'menu' | 'close';
    /** The game's own `GameTopBar` (from a component of its own), in place of the built one. */
    readonly top?: Snippet | undefined;
    readonly above?: Snippet | undefined;
    /** The stage: the game's board, canvas or scene. */
    readonly children: Snippet;
    readonly bottom?: Snippet | undefined;
    readonly side?: Snippet | undefined;
    readonly wide?: 'split' | 'column';
    /** A phone on its side: the stage left, the bar and the tools right (since 1.5.0). */
    readonly landscapeSplit?: boolean;
    /**
     * Two pad sets, player 1's and player 2's (since 1.9.0). Replaces
     * `bottom`; `side` is not drawn, `wide` and `landscapeSplit` do not apply.
     */
    readonly pads?: readonly [Snippet, Snippet] | undefined;
    /** Each set's group name. Default the shell's `gamePads` ("Player 1 controls", "Player 2 controls"). */
    readonly padsLabels?: readonly [string, string] | undefined;
    /** `sides` (default): a column each side of the stage; `ends`: player 2's set along the top, turned. */
    readonly padsLayout?: GamePadsLayout | undefined;
    readonly guard?: GameGuard | undefined;
    readonly testids?:
      | {
          readonly frame?: string;
          readonly topBar?: string;
          readonly menu?: string;
          readonly above?: string;
          readonly stage?: string;
          readonly bottom?: string;
          readonly side?: string;
          /** Player 1's pad slot (since 1.9.0). Default `ui-game-pads-1`. */
          readonly pads1?: string;
          /** Player 2's pad slot (since 1.9.0). Default `ui-game-pads-2`. */
          readonly pads2?: string;
        }
      | undefined;
  }
  const {
    onmenu,
    menuLabel,
    hud,
    trailing,
    strip,
    leading,
    menuGlyph = 'menu',
    top,
    above,
    children,
    bottom,
    side,
    wide = 'split',
    landscapeSplit = false,
    pads,
    padsLabels,
    padsLayout = 'sides',
    guard,
    testids,
  }: Props = $props();

  // ── Two pad sets (1.9.0) ──
  const padded = $derived(pads !== undefined);
  const layoutClass = $derived(
    padded ? `ui-game-frame--pads ui-game-frame--pads-${padsLayout}` : `ui-game-frame--${wide}`,
  );
  const padsLabel = (player: 1 | 2): string =>
    padsLabels?.[player - 1] ?? shellText('gamePads', { n: player });

  // The props stay flat (a game's props may extend them), so both can be
  // passed; `pads` wins, and the game is told once (`GameFramePadsProps` is
  // the compile-time check).
  let warnedPads = false;
  $effect(() => {
    if (warnedPads || pads === undefined || bottom === undefined || !isDev()) return;
    warnedPads = true;
    console.warn('GameFrame: pads replaces bottom; bottom is not drawn while pads are passed.');
  });

  let asking = $state(false);
  let backGuard: BackGuard | null = null;

  const guarded = $derived(guard !== undefined);
  const live = $derived(guard?.live === true);

  /** Every change of `asking` goes through here, so `guard.onasking` hears each one once. */
  function setAsking(next: boolean): void {
    // Untracked: this runs inside the effects below, which must not come to
    // depend on `asking` or on a `guard` object the game makes afresh.
    if (untrack(() => asking) === next) return;
    asking = next;
    untrack(() => guard?.onasking)?.(next);
  }

  $effect(() => {
    if (!guarded) return undefined;
    const current = new BackGuard((next) => {
      setAsking(next);
    });
    backGuard = current;
    return () => {
      current.dispose();
      if (backGuard === current) backGuard = null;
      setAsking(false);
    };
  });

  // Bumped by every "Leave": the guard follows `guard.live` again afterwards.
  let leaves = $state(0);

  $effect(() => {
    void leaves;
    const next = live;
    if (guarded) backGuard?.setLive(next);
  });

  /**
   * The game's own Leave button. `true`: the question is up (or already
   * was); `false`: nothing is live, so the caller leaves at once.
   */
  export function requestLeave(): boolean {
    return backGuard?.request() ?? false;
  }

  /**
   * "Leave": stand the guard down (its entry is given back) and tell the
   * game. The guard is kept, not disposed: a game that stays on this frame
   * (it waits for the server, the leave failed, a new round starts) is
   * guarded again as soon as `guard.live` says so, including at once when it
   * never stopped saying so.
   */
  function confirmLeave(): void {
    const leave = guard?.onleave;
    backGuard?.setLive(false);
    setAsking(false);
    leave?.();
    leaves += 1;
  }
</script>

<div
  class="ui-game-frame {layoutClass}"
  class:ui-game-frame--landscape={landscapeSplit && !padded}
  data-pads={padded ? padsLayout : undefined}
  data-testid={testids?.frame ?? 'ui-game-frame'}
>
  <div class="ui-game-frame-top">
    {#if top !== undefined}
      {@render top()}
    {:else}
      <GameTopBar
        {onmenu}
        {menuLabel}
        {hud}
        {trailing}
        {strip}
        {leading}
        {menuGlyph}
        testid={testids?.topBar ?? 'ui-game-top-bar'}
        menuTestid={testids?.menu ?? 'ui-game-menu'}
      />
    {/if}
  </div>
  {#if above !== undefined}
    <div class="ui-game-frame-above" data-testid={testids?.above ?? 'ui-game-above'}>
      {@render above()}
    </div>
  {/if}
  <div class="ui-game-frame-stage" data-testid={testids?.stage ?? 'ui-game-stage'}>
    {@render children()}
  </div>
  {#if pads !== undefined}
    <!-- Player 1's set, then player 2's: the keyboard's order in every layout. -->
    <div
      class="ui-game-frame-pads ui-game-frame-pads--1"
      role="group"
      aria-label={padsLabel(1)}
      data-testid={testids?.pads1 ?? 'ui-game-pads-1'}
    >
      <div class="ui-game-frame-pads-set">{@render pads[0]()}</div>
    </div>
    <div
      class="ui-game-frame-pads ui-game-frame-pads--2"
      role="group"
      aria-label={padsLabel(2)}
      data-testid={testids?.pads2 ?? 'ui-game-pads-2'}
    >
      <div class="ui-game-frame-pads-set">{@render pads[1]()}</div>
    </div>
  {:else if bottom !== undefined}
    <div class="ui-game-frame-bottom" data-testid={testids?.bottom ?? 'ui-game-bottom'}>
      {@render bottom()}
    </div>
  {/if}
  {#if side !== undefined && pads === undefined}
    <div class="ui-game-frame-side" data-testid={testids?.side ?? 'ui-game-side'}>
      {@render side()}
    </div>
  {/if}
</div>

{#if guard !== undefined}
  <ConfirmDialog
    open={asking}
    title={guard.title}
    body={guard.body}
    confirmLabel={guard.confirmLabel}
    cancelLabel={guard.cancelLabel}
    tone="danger"
    testid={guard.testid}
    onconfirm={confirmLeave}
    oncancel={() => backGuard?.keep()}
  />
{/if}

<style>
  .ui-game-frame {
    /* Fixed to the viewport, not a minimum: a column that may grow pushes
       the tools below the fold on a real phone. The stage absorbs the rest
       and scrolls inside itself. */
    box-sizing: border-box;
    height: 100svh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    background: var(--ui-ground);
    color: var(--ui-ink);
    font-family: var(--ui-font);
  }

  .ui-game-frame-top,
  .ui-game-frame-above,
  .ui-game-frame-bottom {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
  }

  /* The bottom slot is the screen's foot on a phone: it keeps whatever the
     game puts there (a ToolRow, touch pads) clear of the home indicator. A
     ToolRow inside it drops its own allowance, so it is not counted twice. */
  .ui-game-frame-bottom {
    padding-bottom: env(safe-area-inset-bottom);
  }

  .ui-game-frame-bottom :global(.ui-tool-row) {
    padding-bottom: 0.6rem;
  }

  .ui-game-frame-stage {
    position: relative;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .ui-game-frame-side {
    display: none;
  }

  /* A phone on its side (since 1.5.0, opt-in): the stage left, as wide as
     the screen is tall; the bar, above and the tools down a panel on the
     right, the tools at its foot. Below 1024px only: from there the wide
     layouts decide. */
  @media (max-width: 1023.98px) and (max-height: 30rem) and (orientation: landscape) {
    .ui-game-frame--landscape {
      display: grid;
      grid-template-columns: min(100svh, 58vw) minmax(0, 1fr);
      grid-template-rows: minmax(0, min-content) minmax(0, min-content) minmax(0, 1fr) minmax(
          0,
          min-content
        );
      column-gap: 0.75rem;
      padding-left: env(safe-area-inset-left);
      padding-right: env(safe-area-inset-right);
    }

    /* The stage reaches the screen's foot here: it keeps clear of the home
       indicator (the panel's bottom slot already does). */
    .ui-game-frame--landscape > .ui-game-frame-stage {
      grid-column: 1;
      grid-row: 1 / -1;
      padding-bottom: env(safe-area-inset-bottom);
    }

    .ui-game-frame--landscape > .ui-game-frame-top,
    .ui-game-frame--landscape > .ui-game-frame-above {
      grid-column: 2;
    }

    .ui-game-frame--landscape > .ui-game-frame-top {
      grid-row: 1;
    }

    .ui-game-frame--landscape > .ui-game-frame-above {
      grid-row: 2;
    }

    .ui-game-frame--landscape > .ui-game-frame-bottom {
      grid-column: 2;
      grid-row: 4;
    }
  }

  @media (min-width: 1024px) {
    /* The board left, the full height; the rest down a panel on the right.
       Explicit rows that never grow past their content, and one trailing
       row that takes the leftover height and holds nothing: left implicit,
       the browser hands the leftover to the first row and stretches the
       top bar to the board's height. */
    .ui-game-frame--split {
      display: grid;
      grid-template-columns: 1fr 21.25rem;
      grid-template-rows: repeat(4, minmax(0, min-content)) minmax(0, 1fr);
      column-gap: 1.25rem;
      padding: 0 1rem;
    }

    .ui-game-frame--split > .ui-game-frame-stage {
      grid-column: 1;
      grid-row: 1 / -1;
    }

    .ui-game-frame--split > .ui-game-frame-top,
    .ui-game-frame--split > .ui-game-frame-above,
    .ui-game-frame--split > .ui-game-frame-bottom,
    .ui-game-frame--split > .ui-game-frame-side {
      grid-column: 2;
    }

    .ui-game-frame--split > .ui-game-frame-top :global(.ui-game-top) {
      padding-top: calc(0.75rem + env(safe-area-inset-top));
    }

    /* Beside the board the slot is not at the screen's foot. */
    .ui-game-frame--split > .ui-game-frame-bottom {
      padding-bottom: 0;
    }

    .ui-game-frame--split > .ui-game-frame-bottom :global(.ui-tool-row) {
      flex-wrap: wrap;
      justify-content: flex-start;
      padding: 0.5rem 0;
    }

    .ui-game-frame--split > .ui-game-frame-side {
      display: flex;
      flex-direction: column;
    }

    .ui-game-frame--column {
      width: 100%;
      max-width: var(--ui-column);
      margin-inline: auto;
    }
  }

  /* ── Two pad sets (since 1.9.0) ──────────────────────────────────────────
     Only `ui-game-frame--pads` frames: without `pads` nothing here applies,
     and while pads are drawn the frame carries neither `--split`/`--column`
     nor `--landscape`, so the layouts above do not apply either. */

  /* A press in a pad slot never scrolls or zooms the page, and a held pad
     never selects text or raises iOS's long-press callout (which cancels
     the touch). Only the slots: the stage keeps its own touch behaviour (a
     board that scrolls) and every other part its text. No `min-width: 0`:
     a set is never squeezed below its own width (see `sides`). */
  .ui-game-frame-pads {
    box-sizing: border-box;
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    touch-action: none;
    -webkit-user-select: none;
    user-select: none;
    -webkit-touch-callout: none;
  }

  .ui-game-frame-pads-set {
    display: flex;
    flex-direction: column;
  }

  /* A ToolRow in a pad slot drops its own allowance: the slot keeps it. */
  .ui-game-frame-pads :global(.ui-tool-row) {
    padding-bottom: 0.6rem;
  }

  /* `sides`: a column each side of the stage, as wide as the set asks (at
     most 30% of the width), the full height. The bar, `above` and the stage
     in the middle: the bar keeps the top inset, the stage the bottom one.
     A set wider than 30% (its min-content) still gets its whole width (the
     slot's automatic minimum, kept by not setting `min-width: 0`): the
     column grows and the stage shrinks, so no pad is ever drawn over the
     board or past the screen's edge. */
  .ui-game-frame--pads-sides {
    display: grid;
    grid-template-columns: fit-content(30%) minmax(0, 1fr) fit-content(30%);
    grid-template-rows: auto auto minmax(0, 1fr);
  }

  .ui-game-frame--pads-sides > .ui-game-frame-top,
  .ui-game-frame--pads-sides > .ui-game-frame-above,
  .ui-game-frame--pads-sides > .ui-game-frame-stage {
    grid-column: 2;
  }

  .ui-game-frame--pads-sides > .ui-game-frame-top {
    grid-row: 1;
  }

  .ui-game-frame--pads-sides > .ui-game-frame-above {
    grid-row: 2;
  }

  .ui-game-frame--pads-sides > .ui-game-frame-stage {
    grid-row: 3;
    padding-bottom: env(safe-area-inset-bottom);
  }

  .ui-game-frame--pads-sides > .ui-game-frame-pads {
    grid-row: 1 / -1;
    padding-top: env(safe-area-inset-top);
    padding-bottom: env(safe-area-inset-bottom);
  }

  .ui-game-frame--pads-sides > .ui-game-frame-pads--1 {
    grid-column: 1;
    padding-left: env(safe-area-inset-left);
  }

  .ui-game-frame--pads-sides > .ui-game-frame-pads--2 {
    grid-column: 3;
    padding-right: env(safe-area-inset-right);
  }

  /* A phone upright: a column beside a portrait board leaves no board, so
     the two sets share the bottom row, left half and right half. */
  @media (max-width: 599.98px) {
    /* Everything spans the width here, so the frame keeps the side insets
       (and the slots drop their own). */
    .ui-game-frame--pads-sides {
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      grid-template-rows: auto auto minmax(0, 1fr) auto;
      padding-left: env(safe-area-inset-left);
      padding-right: env(safe-area-inset-right);
    }

    .ui-game-frame--pads-sides > .ui-game-frame-top,
    .ui-game-frame--pads-sides > .ui-game-frame-above,
    .ui-game-frame--pads-sides > .ui-game-frame-stage {
      grid-column: 1 / -1;
    }

    .ui-game-frame--pads-sides > .ui-game-frame-stage {
      padding-bottom: 0;
    }

    .ui-game-frame--pads-sides > .ui-game-frame-pads {
      grid-row: 4;
      padding-top: 0;
    }

    .ui-game-frame--pads-sides > .ui-game-frame-pads--1 {
      padding-left: 0;
    }

    .ui-game-frame--pads-sides > .ui-game-frame-pads--2 {
      grid-column: 2;
      padding-right: 0;
    }
  }

  /* `ends`: player 2's set along the top, then the bar, `above`, the stage,
     and player 1's set along the bottom. The visual order only: player 1's
     slot stays first in the document, so first for the keyboard. */
  .ui-game-frame--pads-ends {
    padding-left: env(safe-area-inset-left);
    padding-right: env(safe-area-inset-right);
  }

  .ui-game-frame--pads-ends > .ui-game-frame-pads--2 {
    order: -1;
    padding-top: env(safe-area-inset-top);
  }

  .ui-game-frame--pads-ends > .ui-game-frame-pads--1 {
    padding-bottom: env(safe-area-inset-bottom);
  }

  /* The bar is not at the screen's top here: player 2's set keeps that inset. */
  .ui-game-frame--pads-ends > .ui-game-frame-top :global(.ui-game-top) {
    padding-top: 0.4rem;
  }

  /* Only the set turns, inside its slot: the slot's insets stay on the
     screen's edges, and the set faces the player across the table. */
  .ui-game-frame--pads-ends > .ui-game-frame-pads--2 > .ui-game-frame-pads-set {
    transform: rotate(180deg);
  }
</style>
